import { useEffect, useRef } from 'react'

/**
 * Global keyboard shortcut hook with vim-style sequence support.
 * Bindings are an array of { keys, handler, when? }:
 *   - keys: 'k'  → single keystroke
 *   - keys: 'g d' → leader sequence (press g then d within 1.2s)
 *   - keys: '?' → uppercase / symbol — listens with shift normalised
 *
 * Skips ALL bindings when focus is in an input/textarea/contenteditable so
 * typing isn't intercepted. `when` lets a binding opt out conditionally.
 */
const useKeyboardShortcuts = (bindings, deps = []) => {
  const seqRef = useRef({ buffer: '', timer: null })

  useEffect(() => {
    const isTypingTarget = el => {
      if (!el) return false
      const tag = el.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      if (el.isContentEditable) return true
      return false
    }

    const normalise = e => {
      const key = e.key
      if (key === ' ') return 'space'
      if (key === 'Escape') return 'esc'
      if (key.length === 1) return key.toLowerCase()
      return key.toLowerCase()
    }

    const onKey = e => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (isTypingTarget(e.target)) {
        // Allow `esc` to blur an input even when typing.
        if (e.key !== 'Escape') return
      }

      const k = normalise(e)
      const seq = seqRef.current

      // Build candidate key strings — single + buffered sequence.
      const candidates = []
      if (seq.buffer) candidates.push(`${seq.buffer} ${k}`)
      candidates.push(k)

      // First check sequence bindings, then single-key.
      for (const candidate of candidates) {
        const match = bindings.find(b =>
          b && b.keys === candidate && (b.when ? b.when() : true),
        )
        if (match) {
          e.preventDefault()
          // Clear the sequence buffer (we matched OR a single key consumed it).
          if (seq.timer) clearTimeout(seq.timer)
          seq.buffer = ''
          seq.timer = null
          match.handler(e)
          return
        }
      }

      // If this key opens a sequence (any binding starts with `${k} `),
      // buffer it for 1.2s.
      const opensSequence = bindings.some(b => b && b.keys?.startsWith(`${k} `))
      if (opensSequence) {
        if (seq.timer) clearTimeout(seq.timer)
        seq.buffer = k
        seq.timer = setTimeout(() => { seq.buffer = ''; seq.timer = null }, 1200)
      } else if (seq.buffer) {
        if (seq.timer) clearTimeout(seq.timer)
        seq.buffer = ''
        seq.timer = null
      }
    }

    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      if (seqRef.current.timer) clearTimeout(seqRef.current.timer)
    }
  }, deps)  // eslint isn't enforcing exhaustive-deps in this project
}

export default useKeyboardShortcuts
